import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import keyCodes from "../../constants/keyCodes";

const usePrevious = value => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

const useComboboxHandlers = ({ isExpanded, onHide, onSelect, options }) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeItem, setActiveItem] = useState(null);
  const optionsLength = useMemo(() => options.length, [options]);

  useEffect(() => {
    setActiveIndex(-1);
    setActiveItem(null);
  }, [options, isExpanded]);

  const previousActiveIndex = usePrevious(activeIndex);

  const handleUpPress = useCallback(
    e => {
      e.preventDefault();

      if (activeIndex <= 0) {
        setActiveIndex(optionsLength - 1);
        setActiveItem(options[optionsLength - 1]);
      } else {
        setActiveIndex(activeIndex - 1);
        setActiveItem(options[activeIndex - 1]);
      }
    },
    [activeIndex, optionsLength, options]
  );

  const handleDownPress = useCallback(
    e => {
      e.preventDefault();

      if (activeIndex === -1 || activeIndex >= optionsLength - 1) {
        setActiveIndex(0);
        setActiveItem(options[0]);
      } else {
        setActiveIndex(activeIndex + 1);
        setActiveItem(options[activeIndex + 1]);
      }
    },
    [activeIndex, optionsLength, options]
  );

  const handleReturnPress = e => {
    if (activeItem) {
      onSelect(e, activeItem);
    }
  };

  const handlers = {
    [keyCodes.ESC]: onHide,
    [keyCodes.UP]: handleUpPress,
    [keyCodes.DOWN]: handleDownPress,
    [keyCodes.RETURN]: handleReturnPress
  };

  const callback = e => {
    const key = e.which || e.keyCode;
    const handler = handlers[key];

    if (handler) {
      handler(e);
    }
  };

  return { activeItem, activeIndex, callback, previousActiveIndex };
};

export default useComboboxHandlers;
